// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type ReactNode, useCallback, useMemo, useState } from 'react';

import { notifications } from '@mantine/notifications';
import type { ColDef, ColumnState, FilterModel, GridApi, SortModelItem, IRowNode } from 'ag-grid-enterprise';
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';
import { v4 as uuid } from 'uuid';

import { downloadBlobViaLinkElement } from 'components/Mantine/FileUploads/downloadFileViaLinkElement';
import { doNotSaveColumnIdentifier, baseGridDef } from 'helpers/ag-grid/baseColumnDef';
import { isNotNil } from 'helpers/isNotNil';
import { useGeneralContext } from 'helpers/useGeneralContext';

export const agGridExportFormats = ['csv', 'xlsx'] as const;
export type AgGridExportFormat = (typeof agGridExportFormats)[number];

export type ExportGridOpts<T> = {
  fileName: string;
  gridApi: GridApi<T>;
  format: AgGridExportFormat;
};

type ExportGridContextType = {
  exportGrid: <T>(opts: ExportGridOpts<T>) => Promise<void>;
  exportRunning: boolean;
};

const ExportGridContext = React.createContext<ExportGridContextType | undefined>(undefined);

const pageSize = 100;
const getAllDataFromServerSideDataSource = async <T,>(api: GridApi<T>, startRow = 0): Promise<T[]> =>
  new Promise((resolve, reject) => {
    const endRow = startRow + pageSize;
    const datasource = api.getGridOption('serverSideDatasource');
    if (!datasource) {
      reject('Missing datasource from AG Grid API');
      return;
    }
    datasource.getRows({
      request: {
        startRow,
        endRow,
        filterModel: api.getFilterModel(),
        sortModel: api.getColumnState().filter((c): c is SortModelItem => !!c.sort),
        // Hacky override to get grouped tables to pull "all" data
        // FIXME: This may not work for all cases, revisit in the future
        groupKeys: [`all-rows-${uuid()}`],
        // Unsupported
        rowGroupCols: [],
        pivotCols: [],
        pivotMode: false,
        valueCols: [],
      },
      success: async ({ rowData, rowCount }) => {
        const cleanRowData = (rowData as (T & { __type?: string })[]).filter((row) => row.__type !== 'no-data');
        if (endRow >= (rowCount ?? 0)) {
          resolve(cleanRowData);
        } else {
          resolve([...cleanRowData, ...(await getAllDataFromServerSideDataSource(api, endRow))]);
        }
      },
      api,
      parentNode: {} as IRowNode<T>,
      context: api.getGridOption('context'),
      fail() {
        notifications.show({
          title: 'Something went wrong',
          message: 'Unable to export.',
          color: 'red',
        });
      },
      needsGrandTotal: false,
    });
  });

const blobType: Record<AgGridExportFormat, string> = {
  csv: 'text/csv;charset=utf-8;',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};
export const exportGridViaApi = <T,>(api: GridApi<T>, opts: Pick<ExportGridOpts<T>, 'fileName' | 'format'>) => {
  const { format, fileName } = opts;
  const blob = new Blob(
    [(format === 'xlsx' ? api.getDataAsExcel({ sheetName: fileName }) : api.getDataAsCsv()) ?? ''],
    {
      type: `${blobType[format]}`,
    },
  );
  downloadBlobViaLinkElement(`${fileName}.${format}`, blob);

  // FIXME: Why is the standard AG Grid export not working? The file is empty!
  // api.exportDataAsCsv({ fileName: exportAllDataGridOptions.fileName });
};

export const ExportGridProvider = ({ children }: { children: ReactNode }) => {
  const [exportRunning, setExportRunning] = useState(false);
  const [exportAllDataGridOptions, setExportAllDataGridOptions] = useState<
    | (Pick<AgGridReactProps, 'columnDefs' | 'rowData'> &
        Pick<ExportGridOpts<unknown>, 'fileName' | 'format'> & {
          filterModel: FilterModel;
          columnState: ColumnState[];
          context: unknown;
        })
    | null
  >(null);

  const exportGrid = useCallback<ExportGridContextType['exportGrid']>(async ({ gridApi, ...opts }) => {
    setExportRunning(true);

    const rowData = await getAllDataFromServerSideDataSource(gridApi).catch((e) => {
      setExportRunning(false);
      throw e;
    });

    if (rowData.length === 0) {
      setExportRunning(false);
      notifications.show({
        title: 'Export not run',
        message: 'No data found to export.',
      });
      return;
    }

    setExportAllDataGridOptions({
      rowData,
      columnDefs: gridApi
        .getColumnDefs()
        ?.filter(
          (c: ColDef) =>
            !c.colId?.startsWith(doNotSaveColumnIdentifier) &&
            // Columns *must* have either `field` or `valueGetter` defined to be included in the export
            isNotNil(c.field ?? c.valueGetter),
        )
        .map((c) => ({
          ...c,
          // We can't export cell renderers,
          // so get rid of 'em
          cellRenderer: undefined,
        })),
      columnState: gridApi.getColumnState(),
      filterModel: gridApi.getFilterModel(),
      context: gridApi.getGridOption('context'),
      ...opts,
    });
  }, []);

  const value = useMemo(() => ({ exportGrid, exportRunning }), [exportGrid, exportRunning]);

  return (
    <ExportGridContext value={value}>
      {isNotNil(exportAllDataGridOptions) && (
        <div style={{ display: 'none' }}>
          <AgGridReact
            {...baseGridDef}
            columnDefs={exportAllDataGridOptions.columnDefs}
            rowData={exportAllDataGridOptions.rowData}
            context={exportAllDataGridOptions.context}
            onFirstDataRendered={async ({ api }) => {
              try {
                api.applyColumnState({ state: exportAllDataGridOptions.columnState, applyOrder: true });
                api.setFilterModel(exportAllDataGridOptions.filterModel);

                exportGridViaApi(api, exportAllDataGridOptions);
              } finally {
                setExportAllDataGridOptions(null);
                setExportRunning(false);
              }
            }}
          />
        </div>
      )}
      {children}
    </ExportGridContext>
  );
};

export const useExportGrid = () => useGeneralContext(ExportGridContext, 'ExportGrid');
